import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlass, CircleNotch, Users, Plus, CaretRight, Warning, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { RowActions } from '@/components/shared/RowActions';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { useDebounce } from '@/lib/useDebounce';
import { NewUserWizard } from '@/components/platform/NewUserWizard';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface PlatformUser {
  id: string; email: string; firstName: string; lastName: string; memberType: string;
  status: string; inviteEmailStatus?: string | null; lastLoginAt: string | null;
  createdAt: string; deactivatedAt?: string | null;
  chambers: { id: string; name: string; slug: string; status: string } | null;
}
interface UsersResponse {
  data: PlatformUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const fmtDate = (s: string | null) => (!s ? '—' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
const fmtDateTime = (s: string | null) => (!s ? 'Never' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

function MemberTypeBadge({ type }: { type: string }) {
  const map: Record<string, string> = {
    admin: 'bg-plat-purple-soft text-plat-purple-text', silk: 'bg-plat-info-soft text-plat-info-text', junior: 'bg-plat-info-soft text-plat-info-text',
    pupil: 'bg-plat-info-soft text-plat-info-text', senior_clerk: 'bg-plat-teal-soft text-plat-teal-text', junior_clerk: 'bg-plat-teal-soft text-plat-teal-text',
    staff: 'bg-plat-neutral-soft text-plat-neutral-text', solicitor: 'bg-plat-orange-soft text-plat-orange-text',
  };
  const label = type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${map[type] ?? 'bg-plat-neutral-soft text-plat-neutral-text'}`}>{label}</span>;
}
// Product vocabulary: Active → Deactivated → Reactivated. `inactive` is a deactivated account.
const STATUS_LABELS: Record<string, string> = {
  active: 'Active', invited: 'Invited', suspended: 'Suspended', inactive: 'Deactivated',
};

function StatusBadge({ user }: { user: PlatformUser }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text', invited: 'bg-plat-warning-soft text-plat-warning-text',
    suspended: 'bg-plat-danger-soft text-plat-danger-text', inactive: 'bg-plat-neutral-soft text-plat-fg',
  };
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${map[user.status] ?? 'bg-plat-neutral-soft text-plat-neutral-text'}`}>{STATUS_LABELS[user.status] ?? user.status}</span>;
}

const MEMBER_TYPES = [
  { value: '', label: 'All roles' }, { value: 'admin', label: 'Admin' }, { value: 'silk', label: 'Silk (KC)' },
  { value: 'junior', label: 'Junior Barrister' }, { value: 'pupil', label: 'Pupil' },
  { value: 'senior_clerk', label: 'Senior Clerk' }, { value: 'junior_clerk', label: 'Junior Clerk' }, { value: 'solicitor', label: 'Solicitor' },
];
const STATUSES = [
  { value: '', label: 'All statuses' }, { value: 'active', label: 'Active' }, { value: 'invited', label: 'Invited' },
  { value: 'suspended', label: 'Suspended' }, { value: 'inactive', label: 'Deactivated' },
];

export default function PlatformUsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [memberType, setMemberType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [suspendTarget, setSuspendTarget] = useState<PlatformUser | null>(null);
  const [suspendReason, setSuspendReason] = useState('');

  const debouncedSearch = useDebounce(search, 300);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });

  const { data, isLoading, error } = useQuery<UsersResponse>({
    queryKey: ['platform', 'users', debouncedSearch, memberType, status, page],
    queryFn: async () => {
      // Deactivated accounts are always listed — they stay identifiable (CLAUDE.md #25).
      const res = await platformApi.get('/users', {
        params: {
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(memberType && { memberType }), ...(status && { status }),
          page, limit: 25,
        },
      });
      // listUsers returns { data, pagination }; the controller sends it unwrapped.
      return res.data;
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (v: { id: string; reason: string }) => platformApi.post(`/users/${v.id}/suspend`, { reason: v.reason }),
    onSuccess: () => { toast.success('User suspended'); invalidate(); setSuspendTarget(null); setSuspendReason(''); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Failed to suspend user')),
  });
  const activateMutation = useMutation({
    mutationFn: (id: string) => platformApi.post(`/users/${id}/reactivate`, {}),
    onSuccess: () => { toast.success('Account reactivated — the user can sign in again'); invalidate(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Failed to reactivate the account')),
  });
  const bulkMutation = useMutation({
    mutationFn: (action: 'suspend' | 'activate' | 'resend_invite') =>
      platformApi.post('/users/bulk', { userIds: [...selected], action }),
    onSuccess: (res, action) => {
      const outcomes = (res.data?.data ?? res.data)?.outcomes ?? [];
      const ok = outcomes.filter((o: { ok: boolean }) => o.ok).length;
      toast.success(`${action.replace('_', ' ')}: ${ok}/${outcomes.length} succeeded`);
      invalidate(); setSelected(new Set());
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Bulk action failed')),
  });

  const rows = data?.data ?? [];
  const totalPages = data?.pagination?.totalPages ?? 1;
  const total = data?.pagination?.total ?? 0;

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const onFilter = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => { setter(e.target.value); setPage(1); };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Users</h2>
          <p className="mt-0.5 text-sm text-plat-faint">
            Every barrister and clerk across the platform
            {total > 0 && <span className="ml-2 text-plat-muted">— {total.toLocaleString()} total</span>}
          </p>
        </div>
        <button onClick={() => setWizardOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover">
          <Plus className="h-4 w-4" /> New User
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-plat-faint" />
          <input type="text" placeholder="Search name, email or chambers…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="h-9 w-full rounded-lg border border-plat-border bg-plat-surface pl-9 pr-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-1 focus:ring-plat-focus/50" />
        </div>
        <select value={memberType} onChange={onFilter(setMemberType)} className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-secondary focus:border-plat-focus focus:outline-none">
          {MEMBER_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={status} onChange={onFilter(setStatus)} className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-secondary focus:border-plat-focus focus:outline-none">
          {STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-plat-info-line bg-plat-info-soft px-4 py-2.5 text-sm">
          <span className="text-plat-info-text">{selected.size} selected</span>
          <div className="flex gap-2">
            <button onClick={() => bulkMutation.mutate('activate')} disabled={bulkMutation.isPending} className="rounded-md border border-plat-border bg-plat-surface px-3 py-1.5 text-xs text-plat-secondary hover:bg-plat-hover">Activate</button>
            <button onClick={() => bulkMutation.mutate('resend_invite')} disabled={bulkMutation.isPending} className="rounded-md border border-plat-border bg-plat-surface px-3 py-1.5 text-xs text-plat-secondary hover:bg-plat-hover">Resend invite</button>
            <button onClick={() => bulkMutation.mutate('suspend')} disabled={bulkMutation.isPending} className="rounded-md border border-plat-danger-line bg-plat-danger-soft px-3 py-1.5 text-xs text-plat-danger-text hover:bg-plat-danger-soft">Suspend</button>
          </div>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" /></div>
      ) : error ? (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-5 text-sm text-plat-danger-text">{getPlatformApiErrorMessage(error)}</div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
            {rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14 text-center">
                <Users className="h-8 w-8 text-plat-faint" />
                <p className="text-sm text-plat-faint">No users match your filters.</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-plat-border">
                    <th className="w-10 px-4 py-3.5"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-plat-border bg-plat-surface" /></th>
                    {['User', 'Role', 'Status', 'Organisation', 'Last Login', 'Joined', ''].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-faint">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-plat-border">
                  {rows.map((user) => (
                    <tr key={user.id} className={`transition hover:bg-plat-hover ${user.status === 'inactive' ? 'bg-plat-surface' : ''}`}>
                      <td className="px-4 py-3.5"><input type="checkbox" checked={selected.has(user.id)} onChange={() => toggle(user.id)} className="rounded border-plat-border bg-plat-surface" /></td>
                      <td className="px-5 py-3.5">
                        <button onClick={() => navigate(`/platform/users/${user.id}`)} className="flex items-center gap-3 text-left">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-plat-hover text-xs font-bold text-plat-muted">{user.firstName[0]}{user.lastName[0]}</div>
                          <div>
                            <div className="font-medium text-plat-fg">{user.firstName} {user.lastName}</div>
                            <div className="text-xs text-plat-faint">{user.email}</div>
                          </div>
                        </button>
                      </td>
                      <td className="px-5 py-3.5"><MemberTypeBadge type={user.memberType} /></td>
                      <td className="px-5 py-3.5">
                        <StatusBadge user={user} />
                        {user.inviteEmailStatus === 'failed' && user.status !== 'inactive' && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-plat-warning-text" title="Invitation email failed"><Warning className="h-3 w-3" />email</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {user.chambers ? (
                          <Link
                            to={`/platform/tenants/${user.chambers.id}`}
                            className="text-plat-secondary hover:text-plat-info-text hover:underline"
                          >
                            {user.chambers.name}
                          </Link>
                        ) : <span className="text-plat-faint">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-plat-muted">{fmtDateTime(user.lastLoginAt)}</td>
                      <td className="px-5 py-3.5 text-plat-faint">{fmtDate(user.createdAt)}</td>
                      <td className="px-5 py-3.5 text-right">
                        {user.status === 'suspended' || user.status === 'inactive' ? (
                          <RowActions className="justify-end" onView={() => navigate(`/platform/users/${user.id}`)} onDelete={() => activateMutation.mutate(user.id)} deleteLabel="Reactivate"
                            deleteIcon={<ArrowCounterClockwise className="h-4 w-4" />} deleteTone="neutral"
                            deleteConfirm={{ title: 'Reactivate account?', message: `Reactivate ${user.firstName} ${user.lastName}? They will be able to sign in again.`, confirmLabel: 'Reactivate' }} />
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setSuspendTarget(user); setSuspendReason(''); }} className="text-xs font-medium text-plat-danger-text hover:text-plat-danger-text">Suspend</button>
                            <button onClick={() => navigate(`/platform/users/${user.id}`)} className="text-plat-faint hover:text-plat-muted"><CaretRight className="h-4 w-4" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-plat-faint">
              <span>Page {page} of {totalPages} ({total.toLocaleString()} users)</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-plat-border bg-plat-surface px-3 py-1.5 text-xs text-plat-muted hover:bg-plat-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">Previous</button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-plat-border bg-plat-surface px-3 py-1.5 text-xs text-plat-muted hover:bg-plat-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Suspend reason modal */}
      {suspendTarget && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-2xl border border-plat-border bg-plat-surface p-5 shadow-2xl">
            <h3 className="flex items-center gap-2 text-base font-semibold text-plat-fg"><Warning className="h-4 w-4 text-plat-warning-text" weight="fill" /> Suspend {suspendTarget.firstName} {suspendTarget.lastName}?</h3>
            <p className="mt-1 text-sm text-plat-muted">They will be signed out of all sessions immediately.</p>
            <input value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder="Reason (required)" className="mt-3 h-9 w-full rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSuspendTarget(null)} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-muted hover:bg-plat-hover">Cancel</button>
              <button disabled={suspendReason.trim().length < 3 || suspendMutation.isPending} onClick={() => suspendMutation.mutate({ id: suspendTarget.id, reason: suspendReason.trim() })} className="inline-flex items-center gap-1.5 rounded-lg bg-plat-danger-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-danger-solid-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
                {suspendMutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Suspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      <NewUserWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}
